// LogoAltStep.tsx — step 'logo-alt' del onboarding (vía alternativa al logo).
// Qué hace: deja pegar una URL de logo, subir un archivo, o crear un logo con IA
//   (botón "Create a logo with AI" → setAiLogoOrigin('scan') → step 'ai-logo').
// Qué exporta: LogoAltStep.
// Quién lo usa: CreateWorkspaceModal.

import { RefObject } from 'react';
import { Button } from '../../Button';
import { InputWrapper } from '../../Input';
import { ModalActions } from '../../Modal';
import { WizardStep } from '../types';
import { UrlInput, HiddenInput } from '../styled';

interface Props {
  fileRef: RefObject<HTMLInputElement>;
  logoUrlInput: string;
  setLogoUrlInput: (v: string) => void;
  logoFile: File | null;
  setLogoFile: (f: File | null) => void;
  logoPreview: string;
  setLogoPreview: (v: string) => void;
  setSelectedLogo: (v: string | null) => void;
  setAiLogoOrigin: (v: 'scan' | 'manual') => void;
  confirmLogo: () => void;
  user: (text: string) => void;
  novah: (text: string) => void;
  pushStep: (next: WizardStep) => void;
}

export function LogoAltStep({
  fileRef, logoUrlInput, setLogoUrlInput, logoFile, setLogoFile, logoPreview,
  setLogoPreview, setSelectedLogo, setAiLogoOrigin, confirmLogo, user, novah, pushStep,
}: Props) {
  return (
    <>
      <InputWrapper style={{ marginBottom: 8 }}>
        <UrlInput
          value={logoUrlInput}
          onChange={e => { setLogoUrlInput(e.target.value); setSelectedLogo(e.target.value); }}
          placeholder="Paste logo URL (https://...)"
        />
      </InputWrapper>
      <div style={{ textAlign: 'center', fontSize: 11, color: '#999', margin: '4px 0 8px' }}>or</div>
      <HiddenInput ref={fileRef} type="file" accept="image/*" onChange={e => {
        const f = e.target.files?.[0];
        if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); setSelectedLogo(URL.createObjectURL(f)); }
      }} />
      <Button type="button" $variant="outline" $size="sm" onClick={() => fileRef.current?.click()} style={{ width: '100%', marginBottom: 8 }}>
        {logoFile ? logoFile.name : 'Upload Logo File'}
      </Button>
      <Button type="button" $size="sm" onClick={() => {
        setAiLogoOrigin('scan');
        user("Create a logo with AI");
        novah("Pick a style and I'll design a few logos for you.");
        pushStep('ai-logo');
      }} style={{ width: '100%', marginBottom: 8 }}>
        ✨ Create a logo with AI
      </Button>
      {logoPreview && (
        <div style={{ width: 64, height: 64, margin: '0 auto 8px', border: '2px dashed #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 8 }}>
          <img src={logoPreview} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </div>
      )}
      <ModalActions>
        <Button type="button" $variant="outline" $size="sm" onClick={() => {
          setSelectedLogo(null); setLogoFile(null); setLogoPreview(''); setLogoUrlInput('');
          user('Skipped logo');
          confirmLogo();
        }}>
          Skip
        </Button>
        <Button type="button" $size="sm" onClick={() => {
          // Use URL input or file
          const finalUrl = logoUrlInput.trim() || (logoFile ? logoPreview : null);
          setSelectedLogo(finalUrl);
          user(logoFile ? 'Logo uploaded ✓' : finalUrl ? 'Logo URL added ✓' : 'Skipped logo');
          confirmLogo();
        }} disabled={!logoUrlInput.trim() && !logoFile}>
          Use This Logo
        </Button>
      </ModalActions>
    </>
  );
}
